import React, { useEffect, useState, useCallback } from "react";
import {
  Button,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  CircularProgress,
  useTheme,
} from "@material-ui/core";

import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import JSZip from "jszip";

import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";

import { FileUploadPromise } from "../../../../utils/aws_s3/aws_s3";
import { apiJson } from "../../../../api/Api";

const termsSchema = Yup.object({
  agreeToTerms: Yup.boolean()
    .oneOf([true], "You must accept the terms & conditions")
    .required("Required"),
});

const PDF_URL =
  "https://alisouq-production-files-bucket.s3.eu-central-1.amazonaws.com/static/Alisuq-Vendor-agreement-Auto-Digital-Signature-1%2B(1)+(1).pdf";

const DOCX_URL =
  "https://alisouq-production-files-bucket.s3.eu-central-1.amazonaws.com/static/Alisuq-Vendor-agreement-Auto-Digital-Signature-1%2B(1)%2B(1)+(1)+(1).docx";

const TermsStep = ({ onSuccess, onBack, setIsOnboardingCompleted }) => {
  const theme = useTheme();

  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState("");

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [canAcceptTerms, setCanAcceptTerms] = useState(false);

  const [docxBuffer, setDocxBuffer] = useState(null);

  const [user, setUser] = useState(null);

  const showError = (message) => {
    toast.error(message || "Something went wrong", {
      position: "bottom-right",
      autoClose: 5000,
    });
  };

  // =========================
  // FETCH USER
  // =========================
  const fetchVendor = useCallback(async () => {
    try {
      const response = await apiJson.vendorProfile.getVendor();

      if (response?.success) {
        setUser(response.data);
      } else {
        showError(response?.message);
      }
    } catch (error) {
      console.error("VENDOR FETCH ERROR:", error);
      showError("Failed to fetch vendor details");
    }
  }, []);

  // =========================
  // FETCH DOCX
  // =========================
  const fetchDocx = useCallback(async () => {
    try {
      const response = await fetch(DOCX_URL);

      if (!response.ok) {
        throw new Error("Failed to fetch DOCX");
      }

      const arrayBuffer = await response.arrayBuffer();

      setDocxBuffer(arrayBuffer);
    } catch (error) {
      console.error("DOCX FETCH ERROR:", error);
      showError("Failed to load agreement document");
    }
  }, []);

  // =========================
  // INITIAL LOAD
  // =========================
  useEffect(() => {
    fetchVendor();
    fetchDocx();
  }, [fetchVendor, fetchDocx]);

  // =========================
  // PDF EVENTS
  // =========================
  const handlePageChange = (e) => {
    setCurrentPage(e.currentPage + 1);
  };

  const handleDocumentLoad = (e) => {
    const pages = e.doc.numPages;

    setTotalPages(pages);
  };

  // =========================
  // ENABLE CHECKBOX AFTER FULL VIEW
  // =========================
  useEffect(() => {
    if (totalPages > 0 && currentPage >= totalPages) {
      setCanAcceptTerms(true);
    }
  }, [currentPage, totalPages]);

  // =========================
  // GENERATE AGREEMENT
  // =========================
  const generateAgreement = async () => {
    try {
      if (!docxBuffer) {
        throw new Error("Agreement template not loaded");
      }

      const zip = await JSZip.loadAsync(docxBuffer);

      const docFile = zip.file("word/document.xml");

      if (!docFile) {
        throw new Error("document.xml not found");
      }

      let docXml = await docFile.async("string");

      const replacements = {
        "{{BUSINESS_NAME}}": user?.store?.business_name || "",

        "{{REPRESENTATIVE_NAME}}":
          user?.store?.authorized_signatory_name_en ||
          user?.first_name_en ||
          "",

        "{{BUSINESS_ADDRESS}}": user?.store?.business_address || "",

        "{{SIGNATURE_NAME}}": `${user?.first_name_en || ""} ${
          user?.last_name_en || ""
        }`.trim(),

        "{{SIGN_DATE}}": new Date().toLocaleDateString(),

        "{{LICENSE_NO}}": `${user?.store?.license_no}`
      };

      // Replace placeholders
      Object.entries(replacements).forEach(([key, value]) => {
        docXml = docXml.replaceAll(key, value);
      });

      // Update document.xml
      zip.file("word/document.xml", docXml);

      // Generate DOCX blob
      const blob = await zip.generateAsync({
        type: "blob",
      });

      // CLEAN FILE NAME
      const businessName =
        user?.store?.business_name
          ?.replace(/[^a-zA-Z0-9]/g, "_")
          ?.toLowerCase() || "vendor";

      const fileName = `agreement_${businessName}.docx`;

      // Create proper DOCX file
      const file = new File([blob], fileName, {
        type: `application/ALISUQ_VENDOR_AGREEMENT-${user?.store?.name_en.split(" ").join("_")}.docx`,
      });

      // OPTIONAL LOCAL DOWNLOAD
      const localUrl = URL.createObjectURL(blob);

      const a = document.createElement("a");

      a.href = localUrl;
      a.download = fileName;

      document.body.appendChild(a);

      a.click();

      a.remove();

      URL.revokeObjectURL(localUrl);

      // UPLOAD TO S3
      const uploadResponse = await FileUploadPromise(file);

      console.log("UPLOAD RESPONSE", uploadResponse);

      return uploadResponse?.location || "";
    } catch (error) {
      console.error("AGREEMENT GENERATION ERROR:", error);

      throw error;
    }
  };

  // =========================
  // FORMIK
  // =========================
  const formik = useFormik({
    initialValues: {
      agreeToTerms: false,
    },

    validationSchema: termsSchema,

    onSubmit: async (values) => {
      try {
        setLoading(true);
        setApiError("");

        const agreementUrl = await generateAgreement();

        await apiJson.onboarding.updateTermsConditions({
          agreement_policy_accepted: values.agreeToTerms,

          agreement_policy_url: agreementUrl,

          agreement_policy_accepted_at: new Date(),
        });

        onSuccess();
        setIsOnboardingCompleted(true);
      } catch (error) {
        console.error(error);

        setApiError(
          error?.response?.data?.message ||
            "Something went wrong. Please try again.",
        );
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box className="step-wrapper">
      <Typography className="step-title">Terms & Conditions</Typography>

      <Typography className="step-subtitle">
        Read and accept our vendor agreement to complete your account
        activation.
      </Typography>

      {apiError && (
        <Box className="step-error">
          <Typography>{apiError}</Typography>
        </Box>
      )}

      <form onSubmit={formik.handleSubmit} className="step-form">
        {/* PDF VIEWER */}
        <Box
          style={{
            border: "1px solid rgba(0,0,0,0.2)",
            height: "600px",
            marginBottom: "20px",
          }}
        >
          <Worker workerUrl="https://unpkg.com/pdfjs-dist@2.5.207/build/pdf.worker.min.js">
            <Viewer
              fileUrl={PDF_URL}
              onPageChange={handlePageChange}
              onDocumentLoad={handleDocumentLoad}
            />
          </Worker>
        </Box>

        {/* TERMS CHECKBOX */}
        <Box>
          <FormControlLabel
            control={
              <Checkbox
                name="agreeToTerms"
                checked={formik.values.agreeToTerms}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                color="primary"
                disabled={!canAcceptTerms}
              />
            }
            label="I agree to the Terms & Conditions"
          />

          {!canAcceptTerms && (
            <Typography
              style={{
                fontSize: "12px",
                color: "#999",
                marginTop: "5px",
              }}
            >
              Please scroll through the full agreement before accepting.
            </Typography>
          )}

          {formik.touched.agreeToTerms && formik.errors.agreeToTerms && (
            <FormHelperText error>{formik.errors.agreeToTerms}</FormHelperText>
          )}
        </Box>

        {/* ACTION BUTTONS */}
        <Box className="step-actions">
          <Button onClick={onBack} className="step-back-btn">
            Back
          </Button>

          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !formik.values.agreeToTerms}
            endIcon={
              loading ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            Submit Account For Review
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default TermsStep;
